import type { ReactNode } from "react";
import {
  AiMagicIcon,
  Analytics01Icon,
  BubbleChatIcon,
  CommandLineIcon,
  DistributionIcon,
  Edit02Icon,
  Film01Icon,
  GlobalIcon,
  Layers01Icon,
  MagicWand01Icon,
  Mail01Icon,
  Megaphone01Icon,
  RocketIcon,
  Search01Icon,
  SentIcon,
  SourceCodeIcon,
  SparklesIcon,
  TerminalIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";

import type { IconSvgElement } from "@hugeicons/react";
import { cn } from "@/common/utils";

/* ------------------------------------------------------------------ */
/*  Verified HugeIcons by marketing concept (Stroke / line style).     */
/*  Use these names so every grid stays visually consistent.           */
/* ------------------------------------------------------------------ */

export const ICONS = {
  // ProblemStatement (Section 01)
  everywhere: GlobalIcon,
  noSlop: SparklesIcon,
  runWhereProduct: CommandLineIcon,
  makeYourOwn: MagicWand01Icon,
  // Marketing capability groups
  research: Search01Icon,
  create: Edit02Icon,
  distribute: SentIcon,
  measure: Analytics01Icon,
  engage: BubbleChatIcon,
  outreach: Mail01Icon,
  formats: Film01Icon,
  layers: Layers01Icon,
  megaphone: Megaphone01Icon,
  distribution: DistributionIcon,
  terminal: TerminalIcon,
  sourceCode: SourceCodeIcon,
  rocket: RocketIcon,
  aiMagic: AiMagicIcon,
} as const;

/* ------------------------------------------------------------------ */
/*  FeatureCardGrid — hairline grid wrapper (terax recipe).            */
/* ------------------------------------------------------------------ */

type FeatureCardGridProps = {
  children: ReactNode;
  className?: string;
  cols?: string;
};

export function FeatureCardGrid({
  children,
  className,
  cols = "sm:grid-cols-2 lg:grid-cols-3",
}: FeatureCardGridProps) {
  return (
    <div
      className={cn(
        "grid gap-px overflow-hidden rounded-2xl border border-[var(--cli-border)] bg-[var(--cli-border)] backdrop-blur-sm",
        cols,
        className,
      )}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FeatureCard — single cell with the ported hover (bg lift + icon).  */
/* ------------------------------------------------------------------ */

type FeatureCardProps = {
  icon: IconSvgElement;
  title: ReactNode;
  desc: ReactNode;
  className?: string;
};

export function FeatureCard({
  icon,
  title,
  desc,
  className,
}: FeatureCardProps) {
  return (
    <div
      className={cn(
        "group relative bg-[var(--cli-bg)]/70 p-6 transition-colors hover:bg-[var(--cli-bg-elevated)]",
        className,
      )}
    >
      <div className="inline-flex size-8 items-center justify-center text-[var(--cli-text-secondary)] transition-colors group-hover:text-[var(--cli-text)]">
        <HugeiconsIcon icon={icon} className="size-5" strokeWidth={1.6} />
      </div>
      <div className="mt-5 text-[15px] font-medium tracking-tight text-[var(--cli-text)]">
        {title}
      </div>
      <p className="mt-1.5 text-sm leading-relaxed text-[var(--cli-text-secondary)]">
        {desc}
      </p>
    </div>
  );
}
